/**
 * Cross-module polling hook for async RQ job status.
 *
 * Use this from claims / drawing / spec UIs to attach to an in-flight
 * job via its deterministic rq_job_id. The hook polls
 * ``GET /api/jobs/{job_id}/status`` every ``pollInterval`` ms (5000 by
 * default — matches the existing document-upload polling cadence at
 * UploadedFilesList.tsx:188). Stops polling on terminal status.
 *
 * Refresh-during-run reattach is automatic: the caller stores the
 * job_id (returned in the 202 response from the dual-path route), the
 * UI mounts with that job_id, and the hook resumes polling from
 * wherever the job is in its lifecycle.
 *
 * Returns the latest JobStatus payload, a loading boolean, an error
 * string, and a manual ``refresh`` trigger for use after the drafter
 * cancels a job or wants to re-check immediately.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { getJobStatus, type JobStatus } from '@/api/client';


const TERMINAL_STATUSES = new Set<string>([
  // stage_jobs durable status values
  'succeeded',
  'failed',
  'refused_all_providers',
  'cancelled',
  // legacy RQ-native values for document jobs
  'finished',
  'stopped',
]);


interface UseJobStatusOptions {
  /** Poll cadence in ms. Default 5000 — matches the existing
   *  UploadedFilesList JobProgressPanel cadence. Drawing chains can
   *  run 25+ minutes; faster polling buys no benefit and burns
   *  request budget. */
  pollInterval?: number;
  /** Optional callback fired exactly once when the job lands on a
   *  terminal status. ``status`` carries the final value. */
  onTerminal?: (status: JobStatus) => void;
}


interface UseJobStatusReturn {
  status: JobStatus | null;
  loading: boolean;
  error: string | null;
  /** Manual re-fetch trigger (e.g. drafter clicks "Refresh"). */
  refresh: () => Promise<void>;
  /** True once the job hits a terminal status. The hook stops
   *  polling after that — use this to drive UI transitions out of
   *  the "running" affordance. */
  isTerminal: boolean;
}


export function useJobStatus(
  jobId: string | null,
  { pollInterval = 5000, onTerminal }: UseJobStatusOptions = {},
): UseJobStatusReturn {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(jobId));
  const [error, setError] = useState<string | null>(null);
  // Guard so onTerminal fires exactly once per job_id.
  const terminalNotifiedRef = useRef<string | null>(null);

  const fetchOnce = useCallback(async () => {
    if (!jobId) return;
    try {
      const next = await getJobStatus(jobId);
      setStatus(next);
      setError(null);
      // Fire onTerminal once when crossing into a terminal state.
      if (
        onTerminal &&
        next?.status &&
        TERMINAL_STATUSES.has(next.status) &&
        terminalNotifiedRef.current !== jobId
      ) {
        terminalNotifiedRef.current = jobId;
        try {
          onTerminal(next);
        } catch {
          // onTerminal failure must never block the hook's own
          // state — caller's UI bug shouldn't lock the poller.
        }
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Job status fetch failed';
      setError(msg);
      // Don't clear status on transient errors — the UI keeps
      // showing the last good payload, the error string surfaces
      // alongside.
    } finally {
      setLoading(false);
    }
  }, [jobId, onTerminal]);

  useEffect(() => {
    if (!jobId) {
      setStatus(null);
      setLoading(false);
      setError(null);
      terminalNotifiedRef.current = null;
      return;
    }
    setLoading(true);
    let cancelled = false;
    // Initial fetch immediately so the UI doesn't blank for the
    // full pollInterval on mount.
    void fetchOnce();
    const id = window.setInterval(() => {
      if (cancelled) return;
      // Stop polling once we know the job is terminal — saves
      // request budget + lets the UI settle into its done state.
      if (
        status?.status &&
        TERMINAL_STATUSES.has(status.status)
      ) {
        window.clearInterval(id);
        return;
      }
      void fetchOnce();
    }, pollInterval);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // Intentionally exclude ``status`` — including it would reset
    // the interval every poll. The interval body re-reads status
    // via closure and clears itself on terminal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, pollInterval, fetchOnce]);

  return {
    status,
    loading,
    error,
    refresh: fetchOnce,
    isTerminal: Boolean(status?.status && TERMINAL_STATUSES.has(status.status)),
  };
}


export default useJobStatus;
